'use strict';

import React from 'react';
import _ from 'underscore';

import { object, layout, ajax, console, isServerSide, analytics, searchFilters, memoizedUrlParse } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';

import { navigate } from './../../util';
import { ChartDataController } from './../../viz/chart-data-controller';
import * as BarPlot from './../../viz/BarPlot';
import { termTransformFxnWithOverrides } from './../SearchView';

function getCandidateFields(field, fieldList) {
    if (typeof field !== 'string') return [];

    // Bar plot aggregation fields do not always match the browse facet field exactly
    // (e.g. `sequencing.*` in the chart vs `file_sets.sequencing.*` in facets). Match
    // by equivalent trailing path so chart labels can still use facet label_overrides.
    const matchingFields = fieldList
        .filter((facetField) => {
            if (typeof facetField !== 'string') return false;
            return (
                facetField === field ||
                facetField.endsWith(`.${field}`) ||
                field.endsWith(`.${facetField}`)
            );
        })
        .sort((a, b) => {
            if (a === field) return -1;
            if (b === field) return 1;
            return a.length - b.length;
        });

    return matchingFields.length > 0 ? matchingFields : [field];
}

function termTransformFxnWithChartAliases(facets = null, schemas = null){
    const baseTransform = termTransformFxnWithOverrides(facets);

    const contextFacetFields = facets
        .map(({ field: facetField }) => facetField)
        .filter((facetField) => typeof facetField === 'string');

    // The chart's aggregated fields (tissues, sequencers, assays, etc.) are always
    // File-related, even on Donor/ProtectedDonor browse pages where `context.facets`
    // is the Donor/ProtectedDonor search context and has no such fields at all. Fall
    // back to the File schema's own facets (which is where these label_overrides are
    // actually authored) so charts on those pages can resolve them too.
    const fileSchemaFacets = (schemas && schemas.File && schemas.File.facets) || {};
    const fileSchemaFacetFields = Object.keys(fileSchemaFacets);

    return function(field, key){
        const contextCandidates = getCandidateFields(field, contextFacetFields);
        for (let i = 0; i < contextCandidates.length; i++) {
            const candidateField = contextCandidates[i];
            // Read label overrides directly here so this stays scoped to facet charts and
            // does not broaden behavior of the shared browse/search term transformer.
            const override = facets.find(
                (f) => f.field === candidateField
            )?.label_overrides?.[key];
            if (typeof override !== 'undefined') {
                return override;
            }
        }

        const schemaCandidates = getCandidateFields(field, fileSchemaFacetFields);
        for (let i = 0; i < schemaCandidates.length; i++) {
            const override = fileSchemaFacets[schemaCandidates[i]]?.label_overrides?.[key];
            if (typeof override !== 'undefined') {
                return override;
            }
        }

        return baseTransform(field, key);
    };
}


/**
 * @callback showFunc
 * @param {string} path - Path of current page or view, derived from 'href' prop on FacetCharts component.
 * @return {string|boolean} - The type of view to display ('full', 'small') or bool.
 */

/**
 * Area for displaying Charts rel to browsing.
 * Originally designed as space for 2 charts, a bigger barplot and a smaller/square/circle pie-like chart, to take up 3/4 and 1/4 width of header respectively (where 1/4 of width ~== height).
 * Now the 1/4 area for smaller chart is used for legend & ui controls.
 */
export class FacetCharts extends React.PureComponent {

    /**
     * @type {Object} defaultProps
     * @static
     * @public
     * @member
     * @constant
     * @property {string} [href='/'] Current page/view URL, used to filter charts and pass 'path' arg to props.show, if provided.
     * @property {boolean|string|showFunc} [show] - Type of view to show or whether to display or not; if function supplied, as well as props.href, path is passed as argument.
     * @property {string[]} initialFields - Array with 2 items representing the x-axis field and the group by field.
     */
    static defaultProps = {
        'href' : '/',
        'show' : function(path, search, hash){
            // if (path === '/' || path === '/home') return 'large';
            if (path.indexOf('/browse/') > -1) return true;
            return false;
        },
        'views' : ['small', 'large'],
        'initialFields' : [
            'data_category',
            'data_type'
        ],
        'subBarLayout': 'stacked'
    };

    /**
     * Binds functions and initiates a state object.
     *
     * @member
     * @ignore
     * @public
     * @constructor
     */
    constructor(props){
        super(props);
        this.show = this.show.bind(this);
        this.state = { 'mounted' : false };
    }

    /**
     * Updates `state.mounted`.
     * Initializes ChartDataController if not yet initialized, which fetches data used for charts.
     */
    componentDidMount(){
        var { debug, browseBaseState, initialFields, mapping } = this.props;

        if (!ChartDataController.isInitialized(mapping)){
            ChartDataController.initialize(
                browseBaseState,
                initialFields,
                mapping,
                ()=>{
                    if (debug) console.log("Mounted FacetCharts after initializing ChartDataController:", ChartDataController.getState());
                    setTimeout(() => this.setState({ 'mounted' : true }), 100);
                }
            );
        } else {
            if (debug) console.log('Mounted FacetCharts');
            setTimeout(() => this.setState({ 'mounted' : true }), 100);
        }

    }

    /**
     * @ignore
     */
    componentDidUpdate(pastProps, pastState){
        if (this.props.debug){
            var propKeys    = _.keys(this.props),
                stateKeys   = _.keys(this.state),
                i;
            for (i = 0; i < propKeys.length; i++){
                if (this.props[propKeys[i]] !== pastProps[propKeys[i]]){
                    console.log('DIFFERENT PROP:', propKeys[i], pastProps[propKeys[i]], this.props[propKeys[i]]);
                }
            }
            for (i = 0; i < stateKeys.length; i++){
                if (this.state[stateKeys[i]] !== pastState[stateKeys[i]]){
                    console.log('DIFFERENT STATE:', stateKeys[i], pastState[stateKeys[i]], this.state[stateKeys[i]]);
                }
            }
        }
    }

    /**
     * Given `this.props`, determines if element is currently meant to be invisible (false) or a certain layout ({string}).
     *
     * @instance
     * @private
     * @param {Object} [props=this.props] - Representation of current or next props for this component.
     * @returns {string|boolean} What layout should currently be rendered.
     */
    show(props = this.props){
        if (props.show === false) return false;
        if (typeof props.show === 'string' && props.views.indexOf(props.show) > -1) return props.show;
        if (typeof props.show === 'function') {
            var show;
            if (typeof props.href === 'string') {
                const urlParts = memoizedUrlParse(props.href);
                show = props.show(urlParts.pathname, urlParts.search, urlParts.hash);
            } else {
                show = props.show();
            }
            if (show === false) return false; // If true, continue to use default ('small')
            if (props.views.indexOf(show) > -1) return show;
        }
        return props.views[0]; // Default
    }

    titleMapping = {
        'all' : 'Explore Files',
        'donor' : 'Explore Donors',
        'protected-donor' : 'Explore Donors',
        'file' : 'Explore Files'
    };

    /** Defines buttons/actions to be shown in onHover popover. */
    cursorDetailActions(){
        const { href, browseBaseState, context, mapping = 'all' } = this.props;
        const isBrowseHref = navigate.isBrowseHref(href);
        const currDonorFilters = searchFilters.contextFiltersToExpSetFilters(context && context.filters);
        return [
            {
                'title' : isBrowseHref && this.titleMapping[mapping] ? this.titleMapping[mapping] : 'Browse',
                'function' : function(cursorProps, mouseEvt){
                    var baseParams = navigate.getBrowseBaseParams(browseBaseState, mapping),
                        browseBaseHref = navigate.getBrowseBaseHref(baseParams, mapping);

                    // Reset existing filters if selecting from 'all' view. Preserve if from filtered view.
                    var currentDonorFilters = browseBaseState === 'all' ? {} : currDonorFilters;

                    var newDonorFilters = _.reduce(cursorProps.path, function(donorFilters, node){
                        // Do not change filter IF SET ALREADY because we want to strictly enable filters, not disable any.
                        if(node.field === 'sample_summary.tissues'){
                            if (mapping === 'donor') node.field = 'tissues.tissue_type';
                            if (mapping === 'protected-donor') node.field = 'donor.tissues.tissue_type';
                        }

                        if (donorFilters && donorFilters[node.field] && donorFilters[node.field].has(node.term)){
                            return donorFilters;
                        }
                        return searchFilters.changeFilter(node.field, node.term, donorFilters, null, true);// Existing donorFilters, if null they're retrieved from Redux store, only return new donorFilters vs saving them == set to TRUE
                    }, currentDonorFilters);

                    // very hacky - since Donor and ProtectedDonor lack tissues/sequencers/assays that are associated with released files,
                    // we need to add a external_id filter with all donor ids in the selected tissue
                    // TODO: refactor this when we have a better data model
                    const keysToClear = [
                        'donor.tissues.tissue_type',
                        'tissues.tissue_type',
                        'assays.display_title',
                        'sequencers.display_title',
                    ];

                    if (mapping !== 'all' && keysToClear.some((k) => newDonorFilters[k])) {
                        const last = cursorProps?.path?.at?.(-1) ?? cursorProps?.path?.[cursorProps.path.length - 1];
                        newDonorFilters.external_id = last?.all_donor_ids ?? [];
                        keysToClear.forEach((k) => delete newDonorFilters[k]);
                    }
                    // Reset type filter so it gets set to 'Donor' from browseBaseHref.
                    delete newDonorFilters.type;

                    // Register 'Set Filter' event for each field:term pair (node) of selected Bar Section.
                    _.forEach(cursorProps.path, function(node){
                        analytics.event('cursor_detail', 'BarPlot', 'Set Filter', null, {
                            'name' : analytics.eventLabelFromChartNode(node, false),    // 'New' filter logged here.
                            'field_key'   : node.field,
                            'term_key'    : node.term,
                            'filters'     : analytics.getStringifiedCurrentFilters(currDonorFilters), // 'Existing' filters, or filters at time of action, go here.
                        });
                    });

                    searchFilters.saveChangedFilters(newDonorFilters, browseBaseHref, () => {
                        // Scroll to top of browse page container after navigation is complete.
                        setTimeout(layout.animateScrollTo, 200, "content", Math.abs(layout.getPageVerticalScrollPosition() - 510) * 2, 79);
                    });
                },
                'disabled' : function(cursorProps){
                    if (currDonorFilters && typeof currDonorFilters === 'object'){
                        if (
                            Array.isArray(cursorProps.path) &&
                            (cursorProps.path[0] && cursorProps.path[0].field) &&
                            currDonorFilters[cursorProps.path[0].field] instanceof Set &&
                            currDonorFilters[cursorProps.path[0].field].has(cursorProps.path[0].term) &&
                            (
                                !cursorProps.path[1] || (
                                    cursorProps.path[1].field &&
                                    currDonorFilters[cursorProps.path[1].field] instanceof Set &&
                                    currDonorFilters[cursorProps.path[1].field].has(cursorProps.path[1].term)
                                )
                            )
                        ) return true;
                    }
                    return false;
                }
            }
        ];
    }

    /**
     * @private
     * @returns {JSX.Element} Area with BarPlot chart, wrapped by `ChartDataController.Provider` instance.
     */
    render(){
        const show = this.show();
        if (!show) return null; // We don't show section at all.

        const { context, debug, windowWidth, colWidthPerScreenSize, schemas, href, isFullscreen, mapping = 'all', subBarLayout } = this.props;
        const { mounted } = this.state;

        if (context && context.total === 0) return null;
        if (debug) console.log('WILL SHOW FACETCHARTS', show, href);

        const gridState = layout.responsiveGridState(windowWidth || null);
        const cursorDetailActions = this.cursorDetailActions();
        const browseBaseParams = navigate.getBrowseBaseParams(null, mapping);
        const donorFilters = searchFilters.contextFiltersToExpSetFilters(context && context.filters, browseBaseParams);
        // Use a chart-local term resolver so facet label_overrides are applied in chart bars,
        // popovers, and legend even when chart aggregation fields use an alias of the facet field.
        const termLabelTransform = termTransformFxnWithChartAliases((context && context.facets) || [], schemas);
        let height = show === 'small' ? (mapping === 'all' ? 330 : 370) : 370;
        let width;

        const widthMap = {
            xs: (w) => w - 80,
            sm: (w) => w - 100,
            xxl: (w) => parseInt((w - 320) * 0.75) - 80,
            xl: (w) => parseInt(w * 0.75) - 120,
            lg: (w) => parseInt(w * 0.75) - 80,
            md: (w) => parseInt(w * 0.75) - 80,
        };

        if (widthMap[gridState]) {
            width = widthMap[gridState](windowWidth);
        } else if (isFullscreen) {
            width = parseInt((windowWidth - 40) * 0.75) - 20;
        } else {
            width = parseInt(layout.gridContainerWidth(windowWidth) * 0.75);
        }


        if (mounted && gridState === 'xs') height = Math.min(height, 240);

        //vizUtil.unhighlightTerms();

        if (!mounted){
            return ( // + 30 == breadcrumbs (26) + breadcrumbs-margin-bottom (10) + description (30)
                <div className={"facet-charts loading " + show} key="facet-charts" style={{ 'height' : height }}>
                    <i className="icon icon-spin icon-circle-notch fas" style={{ 'top' : (height / 2 - 30) + 'px' }} />
                </div>
            );
        }

        if (debug) console.log('FacetCharts SCHEMAS AT RENDER', schemas);

        return (
            <div className={"facet-charts show-" + show} key="facet-charts">
                <ChartDataController.Provider id={"barplot-" + mapping}>
                    <BarPlot.UIControlsWrapper legend chartHeight={height} {...{ href, windowWidth, cursorDetailActions, donorFilters, mapping, subBarLayout, termLabelTransform }}>
                        <BarPlot.Chart {...{ width, height, schemas, windowWidth, href, cursorDetailActions, context, termLabelTransform }} />
                    </BarPlot.UIControlsWrapper>
                </ChartDataController.Provider>
            </div>
        );
    }

}
